/**
 * Public storefront filter sidebar (TNK-105 phase 2). Sticky on desktop;
 * slides in from the right on mobile via a Filters button. Sections are
 * collapsible to keep the rail compact when the operator's inventory has
 * many rarity / type values.
 *
 * Filter options are passed in (derived from visible inventory in the
 * parent) so we never offer a value that filters to zero — see
 * features/shopFilters.ts.
 */

import { useState } from "react";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";

import {
  activeFilterCount,
  type FilterOptions,
  type ShopFilterState,
  type ShopRarity,
  type SortBy,
} from "@/features/shopFilters";
import { ManaCost } from "./ManaCost";

interface ShopFiltersProps {
  filters: ShopFilterState;
  options: FilterOptions;
  onChange: (next: ShopFilterState) => void;
  totalCount: number;
  filteredCount: number;
}

const RARITY_COLORS: Record<ShopRarity, string> = {
  Common: "#7a8aaa",
  Uncommon: "#4ade80",
  Rare: "#4d9ef7",
  "Ultra Rare": "#c9a84c",
  "Secret Rare": "#a855f7",
  Legendary: "#f0c96e",
};

function Section({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between py-3"
      >
        <span
          className="shop-display text-[11px] font-semibold uppercase tracking-wider text-primary"
          style={{ letterSpacing: "0.1em" }}
        >
          {title}
        </span>
        <ChevronDown
          size={14}
          className="text-muted-foreground transition-transform"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>
      {open && <div className="pb-3">{children}</div>}
    </div>
  );
}

function MultiToggle<T extends string>({
  options,
  selected,
  onChange,
  colorFor,
}: {
  options: T[];
  selected: T[];
  onChange: (next: T[]) => void;
  colorFor?: (opt: T) => string;
}) {
  const toggle = (opt: T) => {
    if (selected.includes(opt)) onChange(selected.filter((x) => x !== opt));
    else onChange([...selected, opt]);
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const active = selected.includes(opt);
        const color = colorFor?.(opt) ?? "#7a8aaa";
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className="rounded-sm px-2 py-1 text-[11px] transition-colors"
            style={{
              background: active ? `${color}22` : "rgba(255,255,255,0.04)",
              border: active
                ? `1px solid ${color}66`
                : "1px solid rgba(255,255,255,0.08)",
              color: active ? color : "var(--color-muted-foreground)",
              fontWeight: active ? 600 : 400,
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function FilterContents({
  filters,
  options,
  onChange,
  totalCount,
  filteredCount,
}: ShopFiltersProps) {
  const active = activeFilterCount(filters, options.priceCap);
  const clearAll = () =>
    onChange({
      ...filters,
      game: [],
      rarity: [],
      type: [],
      element: [],
      color: [],
      condition: [],
      foilOnly: false,
      priceMin: 0,
      priceMax: options.priceCap,
    });

  // TNK-120 follow-up: when the visible inventory is single-game, the
  // element filter heading reads more naturally as the game's term —
  // "Element" for Pokemon (Fire/Water), "Card Type" for Magic
  // (Creature/Sorcery). Mixed-game view falls back to "Type".
  const elementLabel =
    options.games.length === 1 && options.games[0] === "Pokemon"
      ? "Element"
      : options.games.length === 1 && options.games[0] === "Magic"
        ? "Card Type"
        : "Type";

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border py-3">
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={14} className="text-primary" />
          <span className="shop-display text-[13px] font-bold text-foreground">
            Filters
          </span>
          {active > 0 && (
            <span
              className="shop-mono rounded-sm px-1.5 py-0.5 text-[10px]"
              style={{
                background: "rgba(201,168,76,0.2)",
                color: "var(--color-primary)",
              }}
            >
              {active}
            </span>
          )}
        </div>
        {active > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <X size={12} /> Clear
          </button>
        )}
      </div>

      {/* Results count — TNK-116: drop the catalog total, keep just the
          filtered match count so buyers see "53 results" while filtering.
          When no filter is active (filteredCount === totalCount), the
          panel reads blank to avoid surfacing the catalog size. */}
      {filteredCount < totalCount && (
        <div className="border-b border-border py-2.5">
          <p className="shop-mono text-[11px] text-muted-foreground">
            <span className="font-semibold text-primary">{filteredCount}</span>
            <span> matching</span>
          </p>
        </div>
      )}

      <Section title="Sort By">
        <select
          value={filters.sortBy}
          onChange={(e) =>
            onChange({ ...filters, sortBy: e.target.value as SortBy })
          }
          className="w-full rounded-sm border border-border bg-muted px-2 py-2 text-xs text-foreground focus:border-primary focus:outline-none"
        >
          <option value="newest">Newest First</option>
          <option value="price-asc">Price: Low to High</option>
          <option value="price-desc">Price: High to Low</option>
          <option value="name-asc">Name A–Z</option>
          <option value="rarity">By Rarity</option>
        </select>
      </Section>

      <Section title="Price Range">
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <label className="text-[10px] text-muted-foreground">Min</label>
            <input
              type="number"
              min={0}
              value={filters.priceMin}
              onChange={(e) =>
                onChange({ ...filters, priceMin: Number(e.target.value) || 0 })
              }
              className="shop-mono mt-0.5 w-full rounded-sm border border-border bg-muted px-2 py-1.5 text-xs text-foreground focus:border-primary focus:outline-none"
            />
          </div>
          <div className="flex-1">
            <label className="text-[10px] text-muted-foreground">Max</label>
            <input
              type="number"
              min={0}
              value={filters.priceMax}
              onChange={(e) =>
                onChange({ ...filters, priceMax: Number(e.target.value) || 0 })
              }
              className="shop-mono mt-0.5 w-full rounded-sm border border-border bg-muted px-2 py-1.5 text-xs text-foreground focus:border-primary focus:outline-none"
            />
          </div>
        </div>
      </Section>

      {options.games.length > 1 && (
        <Section title="Game">
          <MultiToggle
            options={options.games}
            selected={filters.game}
            onChange={(v) => onChange({ ...filters, game: v })}
          />
        </Section>
      )}

      {options.rarities.length > 1 && (
        <Section title="Rarity">
          <MultiToggle
            options={options.rarities}
            selected={filters.rarity}
            onChange={(v) => onChange({ ...filters, rarity: v })}
            colorFor={(r) => RARITY_COLORS[r]}
          />
        </Section>
      )}

      {/*
        TNK-124 follow-up: split the cross-game `types` axis into clean
        per-game pill rows. The "Class" row carries only Pokémon
        supertypes (Pokémon / Trainer / Energy) — Magic card types
        live in their own "Card Type" row below. Both write into
        `filters.type` because both come from the same DB column
        (`tcg_supertype`); the merge logic preserves selections from
        the other row when the buyer toggles a pill.
      */}
      {(() => {
        const pokemonClasses = options.types.filter((v) =>
          new Set(["Pokémon", "Pokemon", "Trainer", "Energy"]).has(v),
        );
        if (pokemonClasses.length === 0) return null;
        return (
          <Section title="Class">
            <MultiToggle
              options={pokemonClasses}
              selected={filters.type.filter((v) => pokemonClasses.includes(v))}
              onChange={(v) => {
                const others = filters.type.filter(
                  (x) => !pokemonClasses.includes(x),
                );
                onChange({ ...filters, type: [...others, ...v] });
              }}
            />
          </Section>
        );
      })()}

      {options.magicCardTypes.length > 0 && (
        <Section title="Card Type">
          <MultiToggle
            options={options.magicCardTypes}
            selected={filters.type.filter((v) =>
              options.magicCardTypes.includes(v),
            )}
            onChange={(v) => {
              const others = filters.type.filter(
                (x) => !options.magicCardTypes.includes(x),
              );
              onChange({ ...filters, type: [...others, ...v] });
            }}
          />
        </Section>
      )}

      {/*
        Pokemon energies live in `tcg_types` (jsonb array column), so
        their pills write into `filters.element` rather than .type.
        Magic card types DO NOT live here — they live in tcg_supertype
        and route through "Card Type" above.
      */}
      {options.pokemonEnergies.length > 0 && (
        <Section title="Element">
          <MultiToggle
            options={options.pokemonEnergies}
            selected={filters.element.filter((v) =>
              options.pokemonEnergies.includes(v),
            )}
            onChange={(v) => {
              const others = filters.element.filter(
                (x) => !options.pokemonEnergies.includes(x),
              );
              onChange({ ...filters, element: [...others, ...v] });
            }}
          />
        </Section>
      )}

      {/* Fallback to the legacy combined section when the server hasn't
          returned the split axes yet (older API) AND there are values to
          show. Keeps shared deployments compatible. */}
      {options.pokemonEnergies.length === 0 &&
        options.magicCardTypes.length === 0 &&
        options.elements.length > 1 && (
          <Section title={elementLabel}>
            <MultiToggle
              options={options.elements}
              selected={filters.element}
              onChange={(v) => onChange({ ...filters, element: v })}
            />
          </Section>
        )}

      {options.colors.length > 0 && (
        <Section title="Color">
          <div className="flex flex-wrap gap-1.5">
            {options.colors.map((c) => {
              const active = filters.color.includes(c);
              const toggle = () => {
                if (active)
                  onChange({
                    ...filters,
                    color: filters.color.filter((x) => x !== c),
                  });
                else onChange({ ...filters, color: [...filters.color, c] });
              };
              return (
                <button
                  key={c}
                  type="button"
                  onClick={toggle}
                  aria-label={`Filter by ${c}`}
                  className="flex items-center gap-1.5 rounded-sm px-2 py-1 text-[11px] transition-colors"
                  style={{
                    background: active
                      ? "rgba(201,168,76,0.18)"
                      : "rgba(255,255,255,0.04)",
                    border: active
                      ? "1px solid rgba(201,168,76,0.45)"
                      : "1px solid rgba(255,255,255,0.08)",
                    color: active ? "#f0c96e" : "var(--color-muted-foreground)",
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  <ManaCost cost={`{${c}}`} size={14} />
                </button>
              );
            })}
          </div>
        </Section>
      )}

      {/* TNK-116: always surface the Condition filter when any condition
          is present (was previously >1, which hid the section in an
          NM-only store and made buyers wonder if all cards were the
          same condition by accident). Single-option behaves as a
          confirmation pill rather than a hidden filter. */}
      {options.conditions.length > 0 && (
        <Section title="Condition">
          <MultiToggle
            options={options.conditions}
            selected={filters.condition}
            onChange={(v) => onChange({ ...filters, condition: v })}
          />
        </Section>
      )}

      <Section title="Other">
        <label className="flex cursor-pointer items-center gap-2">
          <div
            onClick={() =>
              onChange({ ...filters, foilOnly: !filters.foilOnly })
            }
            className="relative flex-shrink-0"
            style={{
              width: "32px",
              height: "18px",
              borderRadius: "9px",
              background: filters.foilOnly
                ? "rgba(201,168,76,0.3)"
                : "rgba(255,255,255,0.08)",
              border: filters.foilOnly
                ? "1px solid rgba(201,168,76,0.5)"
                : "1px solid rgba(255,255,255,0.1)",
              transition: "all 0.2s",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "2px",
                left: filters.foilOnly ? "16px" : "2px",
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                background: filters.foilOnly
                  ? "var(--color-primary)"
                  : "var(--color-muted-foreground)",
                transition: "all 0.2s",
              }}
            />
          </div>
          <span className="text-xs text-foreground">Foil / Holo only</span>
        </label>
      </Section>
    </div>
  );
}

export function ShopFilters(props: ShopFiltersProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const active = activeFilterCount(props.filters, props.options.priceCap);

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className="sticky top-6 hidden w-56 flex-shrink-0 self-start overflow-y-auto pr-1 lg:block"
        style={{ maxHeight: "calc(100vh - 120px)" }}
      >
        <FilterContents {...props} />
      </aside>

      {/* Mobile drawer trigger + drawer */}
      <div className="mb-4 lg:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="flex items-center gap-2 rounded-sm px-4 py-2 text-sm"
          style={{
            background: "rgba(201,168,76,0.1)",
            border: "1px solid rgba(201,168,76,0.3)",
            color: "var(--color-primary)",
          }}
        >
          <SlidersHorizontal size={14} />
          Filters{active > 0 ? ` (${active})` : ""}
        </button>

        {mobileOpen && (
          <div className="fixed inset-0 z-50 flex">
            <div
              className="absolute inset-0 bg-black/70"
              onClick={() => setMobileOpen(false)}
            />
            <div
              className="relative ml-auto h-full w-72 overflow-y-auto p-4"
              style={{
                background: "var(--color-card)",
                borderLeft: "1px solid var(--color-border)",
              }}
            >
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"
                aria-label="Close filters"
              >
                <X size={18} />
              </button>
              <FilterContents {...props} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
